import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, Gift, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const QUESTIONS = [
  { key: "goal", q: "What's your main goal?", opts: ["Weight Loss", "Fat Loss", "Build Muscle", "Strength", "General Fitness", "Running / HYROX"] },
  { key: "experience", q: "How would you describe your experience?", opts: ["Complete beginner", "Some experience", "Consistent 1+ year", "Advanced athlete"] },
  { key: "activity_level", q: "How active is your daily life?", opts: ["Mostly sedentary (desk job)", "Lightly active", "Moderately active", "Very active"] },
  { key: "time_per_day", q: "How much time can you commit daily?", opts: ["15-20 min", "30-45 min", "45-60 min", "60+ min"] },
  { key: "obstacle", q: "What's held you back before?", opts: ["Staying consistent", "Not knowing what to do", "No time", "Injuries / health", "Nothing — I'm ready!"] },
];

export default function Assessment() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const total = QUESTIONS.length + 1;
  const progress = Math.round(((step) / total) * 100);

  const pick = (key, val) => { setAnswers((a) => ({ ...a, [key]: val })); setTimeout(() => setStep((s) => s + 1), 220); };

  const submit = async () => {
    if (!lead.name || !lead.email) { toast.error("Please enter your name and email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/assessment", { ...lead, ...answers });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (result) return (
    <div data-testid="assessment-result" className="min-h-screen pt-28 pb-20 bg-background">
      <div className="mx-auto max-w-2xl px-5 text-center">
        <div className="grid place-items-center w-16 h-16 rounded-full bg-primary/10 text-primary mx-auto mb-6"><CheckCircle2 size={30} /></div>
        <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold">Your Result</p>
        <h1 className="font-display font-900 uppercase text-4xl md:text-6xl tracking-tight mt-3 leading-none">We recommend the<br /><span className="text-primary">{result.recommended}</span></h1>
        <p className="text-muted-foreground mt-5">Based on your {result.goal.toLowerCase()} goal, this plan gives you the fastest, most sustainable path. Here's what to do next:</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-9 text-left">
          <div className="border border-border rounded-lg p-6 bg-card">
            <Gift className="text-primary mb-3" />
            <h3 className="font-display font-700 uppercase text-xl">Free 7-Day Challenge</h3>
            <p className="text-muted-foreground text-sm mt-1">Create your free account to unlock a guided 7-day starter challenge.</p>
            <Link to="/register" data-testid="result-register" className="inline-block mt-4 bg-primary text-primary-foreground px-6 py-3 rounded-full text-xs uppercase tracking-[0.12em] font-bold">Start Free</Link>
          </div>
          <div className="border border-border rounded-lg p-6 bg-card">
            <CalendarCheck className="text-primary mb-3" />
            <h3 className="font-display font-700 uppercase text-xl">Book a Consult</h3>
            <p className="text-muted-foreground text-sm mt-1">Prefer 1:1 guidance? Book a free consultation with the coach.</p>
            <Link to="/booking" data-testid="result-book" className="inline-block mt-4 border border-border px-6 py-3 rounded-full text-xs uppercase tracking-[0.12em] font-bold hover:border-primary transition-colors">Book Now</Link>
          </div>
        </div>
      </div>
    </div>
  );

  const isLeadStep = step === QUESTIONS.length;

  return (
    <div data-testid="assessment-page" className="min-h-screen pt-24 pb-16 bg-background">
      <div className="mx-auto max-w-2xl px-5">
        <div className="h-1.5 bg-secondary rounded-full overflow-hidden mb-10">
          <motion.div className="h-full bg-primary" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35 }}>
            {!isLeadStep ? (
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-3">Question {step + 1} of {QUESTIONS.length}</p>
                <h2 className="font-display font-800 uppercase text-4xl md:text-5xl tracking-tight leading-none">{QUESTIONS[step].q}</h2>
                <div className="grid gap-3 mt-8">
                  {QUESTIONS[step].opts.map((o) => (
                    <button key={o} data-testid={`quiz-option-${o}`} onClick={() => pick(QUESTIONS[step].key, o)}
                      className={`text-left px-6 py-5 rounded-lg border text-lg font-medium transition-colors ${answers[QUESTIONS[step].key] === o ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary"}`}>{o}</button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-3">Almost done</p>
                <h2 className="font-display font-800 uppercase text-4xl md:text-5xl tracking-tight leading-none">Where should we send your plan?</h2>
                <div className="space-y-4 mt-8">
                  <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Name</Label><Input data-testid="assessment-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="mt-2 h-12" /></div>
                  <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Email</Label><Input data-testid="assessment-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="mt-2 h-12" /></div>
                  <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Phone (optional)</Label><Input data-testid="assessment-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="mt-2 h-12" /></div>
                  <button data-testid="assessment-submit" onClick={submit} disabled={busy} className="w-full bg-primary text-primary-foreground py-4 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.02] transition-transform disabled:opacity-60 flex items-center justify-center gap-2">{busy ? "Analysing…" : <>Get My Result <ArrowRight size={16} /></>}</button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        {step > 0 && (
          <button onClick={() => setStep((s) => s - 1)} className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary transition-colors"><ArrowLeft size={14} /> Back</button>
        )}
      </div>
    </div>
  );
}
